import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  clearAllNotifications,
  deleteNotification,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/api/notificationsApi";
import { notificationsKeys } from "@/api/notificationsKeys";
import type { NotificationDto, NotificationsListResponse } from "@/types/api/notifications";

export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey: notificationsKeys.list(),
    queryFn: getNotifications,
    enabled,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: (row) => {
      queryClient.setQueryData<NotificationsListResponse>(notificationsKeys.list(), (current) => {
        if (!current) return current;
        const wasUnread = current.items.some((item) => item.id === row.id && item.readAt == null);
        return {
          unreadCount: wasUnread ? Math.max(0, current.unreadCount - 1) : current.unreadCount,
          items: current.items.map((item) => (item.id === row.id ? row : item)),
        };
      });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.setQueryData<NotificationsListResponse>(notificationsKeys.list(), (current) => {
        if (!current) return current;
        const now = new Date().toISOString();
        return {
          unreadCount: 0,
          items: current.items.map(
            (item): NotificationDto => (item.readAt ? item : { ...item, readAt: now }),
          ),
        };
      });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNotification,
    onSuccess: (_void, id) => {
      queryClient.setQueryData<NotificationsListResponse>(notificationsKeys.list(), (current) => {
        if (!current) return current;
        const removed = current.items.find((item) => item.id === id);
        return {
          unreadCount:
            removed && removed.readAt == null
              ? Math.max(0, current.unreadCount - 1)
              : current.unreadCount,
          items: current.items.filter((item) => item.id !== id),
        };
      });
    },
  });
}

export function useClearAllNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearAllNotifications,
    onSuccess: () => {
      queryClient.setQueryData<NotificationsListResponse>(notificationsKeys.list(), {
        items: [],
        unreadCount: 0,
      });
    },
  });
}
